import { computed, Injectable, signal } from '@angular/core';

/** O client OIDC do aprova-web (IA mode): quem pede o login por ele está no shell. */
export const IA_MODE_CLIENT_ID = 'aprova-web';

const STORAGE_KEY = 'aprova-login:ia-mode';
const MOBILE_QUERY = '(max-width: 767px)';

/**
 * O `returnTo` é o /oauth/authorize que retoma o fluxo OIDC. Chega codificado uma ou duas
 * vezes (o 2FA recodifica), então decodifica até virar URL.
 */
export function requestedByIaMode(returnTo: string | null | undefined): boolean {
  let value = returnTo ?? '';
  for (let attempt = 0; attempt < 3 && value; attempt++) {
    try {
      return new URL(value).searchParams.get('client_id') === IA_MODE_CLIENT_ID;
    } catch {
      const decoded = safeDecode(value);
      if (!decoded || decoded === value) {
        return false;
      }
      value = decoded;
    }
  }
  return false;
}

function safeDecode(value: string): string | null {
  try {
    return decodeURIComponent(value);
  } catch {
    return null;
  }
}

/**
 * APR-4028 — o layout de celular do login vale quando o login foi pedido pelo aprova-web
 * (IA mode) e a tela é de celular, a mesma régua do Angular embutido. O `returnTo` só chega
 * à tela de login, então a marca fica na aba e vale também no cadastro e na recuperação.
 */
/** Etapa do cadastro mostrada no cabeçalho escuro do celular. */
export interface IaModeStep {
  current: number;
  total: number;
  title: string;
}

@Injectable({ providedIn: 'root' })
export class IaModeMobileLayout {
  private readonly mobile = signal(this.matchesMobile());
  private readonly fromIaMode = signal(this.readMark());

  public readonly active = computed(() => this.mobile() && this.fromIaMode());

  /** Preenchida pelo cadastro; fora dele o cabeçalho não mostra etapa. */
  public readonly step = signal<IaModeStep | null>(null);

  /** O voltar do cabeçalho, quando a tela tem o seu (a etapa anterior do cadastro). */
  public readonly back = signal<(() => void) | null>(null);

  constructor() {
    window.matchMedia?.(MOBILE_QUERY)?.addEventListener?.('change', (event) => this.mobile.set(event.matches));
  }

  /** Lê o `returnTo` da URL de entrada e guarda a marca na aba quando o pedido veio do IA mode. */
  remember(search: string): void {
    if (!requestedByIaMode(new URLSearchParams(search).get('returnTo'))) {
      return;
    }
    try {
      sessionStorage.setItem(STORAGE_KEY, '1');
    } catch {
      // Storage bloqueado: a marca vale só nesta tela.
    }
    this.fromIaMode.set(true);
  }

  private matchesMobile(): boolean {
    return window.matchMedia?.(MOBILE_QUERY)?.matches ?? false;
  }

  private readMark(): boolean {
    try {
      return sessionStorage.getItem(STORAGE_KEY) === '1';
    } catch {
      return false;
    }
  }
}
